import { ReactNode, RefObject, useMemo } from 'react'
import { GenericObject, ObjectProperty, ObjectPropertyValue } from '../../shared/types'
import { ObjectTypes } from '../api/object'
import { DetailLayout, LayoutElementComponent } from '../layouts/types'
import { FORMATTER } from '../objects/formatters'
import { ObjectTooltip } from '../objects/object-tooltip'
import { ChildObject } from './child-object'
import { DateFormatter } from './date-formatter'
import { DatePicker } from './date-picker'
import { InPlace } from './in-place'
import { Input } from './input'
import { MultiSelect } from './multi-select'
import { PluginInput } from './plugin-input'
import { Options, Select } from './select'
import { Switch } from './switch'
import { Textarea } from './textarea'
import { Tooltip, TooltipWrap } from './tooltip'

type ObjectPropertyInputProps = Omit<ObjectProperty, 'id' | 'object' | 'type'> & {
	value?: ObjectPropertyValue
	type: LayoutElementComponent['type']
	onChange: (value: ObjectPropertyValue) => void
	defaultValue?: ObjectPropertyValue
	tooltip?: DetailLayout
	inputProps?: Record<string, string>
	data?: GenericObject
	formatter?: string
}

export function ObjectPropertyFieldInPlace({
	name,
	type,
	options,
	value,
	onChange,
	referenceTo,
	isPositive,
	defaultValue,
	tooltip,
	isEditable,
	formatter,
	inputProps,
	data,
}: ObjectPropertyInputProps) {
	const selectOptions = useMemo(() => {
		return options?.map(option => ({ value: option.id as string, label: option.name }))
	}, [options])

	const effectiveValue = value ?? defaultValue ?? ''

	const selectedValue = useMemo(() => {
		if (Array.isArray(effectiveValue)) {
			return options
				?.filter(option => effectiveValue.includes(option.id as string))
				.map(option => option.name)
				.join(', ')
		}
		if (effectiveValue && options) {
			return options.find(option => option.id === effectiveValue)?.name
		}
		return null
	}, [options, effectiveValue])

	const formattedValue = useMemo(() => {
		if (formatter) {
			return FORMATTER[formatter as Exclude<keyof typeof FORMATTER, 'reference'>](
				effectiveValue,
				undefined,
				data,
			)
		}
		return effectiveValue
	}, [effectiveValue, formatter, data])

	switch (type) {
		case 'string':
		case 'number':
		case 'currency':
			if (isEditable === false) {
				return <>{formattedValue || <em className="text-gray-400">nevyplněno</em>}</>
			}
			return (
				<InPlace
					onToggle={ref => {
						const newValue = (ref.current as HTMLInputElement).value
						// intentionally not using === to simplify for numbers and empty strings
						if (newValue != effectiveValue) {
							onChange(newValue)
						}
					}}
				>
					{formattedValue || <em className="text-gray-400">nevyplněno</em>}
					{({ ref, onClickOutside }) => (
						<Input
							innerRef={ref as RefObject<HTMLInputElement>}
							aria-label={name}
							maxLength={255}
							type={type === 'number' ? type : 'text'}
							min={isPositive ? 0 : undefined}
							defaultValue={effectiveValue as string}
							onKeyDown={e => {
								if (e.key === 'Enter') {
									onClickOutside()
								}
							}}
						/>
					)}
				</InPlace>
			)
		case 'text':
			if (isEditable === false) {
				return <>{formattedValue || <em className="text-gray-400">nevyplněno</em>}</>
			}
			return (
				<InPlace
					onToggle={ref => {
						const newValue = (ref.current as HTMLInputElement).value
						if (newValue !== effectiveValue) {
							onChange(newValue)
						}
					}}
				>
					{formattedValue || <em className="text-gray-400">nevyplněno</em>}
					{({ ref }) => (
						<Textarea
							innerRef={ref as RefObject<HTMLTextAreaElement>}
							aria-label={name}
							maxLength={255}
							defaultValue={effectiveValue as string}
						/>
					)}
				</InPlace>
			)
		case 'single':
			return (
				<InPlace>
					{selectedValue}
					<Select
						options={selectOptions as Options}
						selectedKey={effectiveValue as string}
						onSelectionChange={value => {
							if (effectiveValue !== value) {
								onChange(value)
							}
						}}
						autoFocus
					/>
				</InPlace>
			)
		case 'multi':
			return (
				<InPlace>
					{selectedValue}
					<MultiSelect
						selectedKeys={(effectiveValue ?? []) as string[]}
						options={selectOptions as Options}
						onSelectionChange={
							onChange as (selectedKeys: Array<string | number>) => void
						}
						autoFocus
					/>
				</InPlace>
			)
		case 'datetime':
			if (isEditable === false) return <DateFormatter date={effectiveValue as string} />
			// ignore for now
			return (
				<InPlace>
					<DateFormatter date={effectiveValue as string} />
					<DatePicker
						defaultValue={effectiveValue as string}
						onChange={onChange as (value: string) => void}
					/>
				</InPlace>
			)
		case 'reference': {
			if (!referenceTo) return null
			const formatter = FORMATTER.reference[referenceTo]
			const renderedValue = formatter(effectiveValue, undefined, data)

			const withTooltip = (value: ReactNode) => {
				if (!tooltip) return value
				if (!effectiveValue) return value
				return (
					<Tooltip
						content={
							<ObjectTooltip
								object={referenceTo}
								id={effectiveValue as string}
								config={tooltip}
							/>
						}
						delay={250}
					>
						<TooltipWrap>
							<div className="text-sm">{value}</div>
						</TooltipWrap>
					</Tooltip>
				)
			}

			if (isEditable === false) {
				return withTooltip(renderedValue)
			}

			return (
				<InPlace>
					{withTooltip(renderedValue)}
					<ChildObject
						aria-label={name}
						selectedKey={effectiveValue as string | null}
						onSelectionChange={value => {
							if (effectiveValue !== value) {
								onChange(value)
							}
						}}
						object={referenceTo}
						autoFocus
					/>
				</InPlace>
			)
		}
		case 'boolean':
			return (
				<Switch
					name={name}
					defaultSelected={effectiveValue as boolean}
					onChange={onChange}
				/>
			)
		case 'plugin':
			return (
				<InPlace>
					{formattedValue || <em className="text-gray-400">nevyplněno</em>}
					<PluginInput
						aria-label={name}
						name={name}
						data={data}
						{...(inputProps as {
							id: string
							action: string
							object: keyof ObjectTypes
						})}
						defaultValue={effectiveValue as string | undefined}
						onChange={value => {
							if (effectiveValue !== value) {
								onChange(value)
							}
						}}
					/>
				</InPlace>
			)
		case 'derived':
			return formattedValue
		case 'cloudinary':
			return null
	}
}
